import { zodResolver } from '@hookform/resolvers/zod';
import {
  makeLoginFormSchema,
  LoginFormValues,
} from '@porterin/booking-flow/src/auth/domain/validations/LoginValidationSchema';
import AuthBanner from '@porterin/booking-flow/src/auth/view/components/common/AuthBanner';
import AuthCloseCmp from '@porterin/booking-flow/src/auth/view/components/common/AuthCloseCmp';
import LoginFormFields from '@porterin/booking-flow/src/auth/view/components/login-modal/LoginFormFields';
import { useAuthContentContext } from '@porterin/booking-flow/src/auth/view/contexts/auth-content/AuthContentContext';
import {
  GaEvents,
  MoeEvents,
} from '@porterin/booking-flow/src/auth/view/stores/AuthAnalyticsStore';
import { PrefillData } from '@porterin/booking-flow/src/auth/view/types/SignupModal';
import {
  mapPageToCategory,
  makeBkfEventParameterPayload,
} from '@porterin/booking-flow/src/utils/BookingFlowAnalyticsHelper';
import {
  publishEvent,
  publishEventParameter,
} from '@porterin/component-library/src/ts-utils/analytics/react-ga/EventTracker';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import AuthTextDescCmp from '../common/AuthTextDescCmp';
import { AuthModalConfig } from '../../../domain/types/AuthModalConfig';

export interface LoginModalProps {
  prefill_data: PrefillData | null;
  onFormSubmit: (
    data: LoginFormValues,
    is_mobile_updated: boolean,
    set_error: (error: string) => void,
  ) => void;
  closeModal: () => void;
  config: AuthModalConfig;
}

const LoginModal = ({
  prefill_data,
  onFormSubmit,
  closeModal,
  config,
}: LoginModalProps) => {
  const { loginModal } = useAuthContentContext();
  const form = useForm<LoginFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(makeLoginFormSchema(config.mobileRegex)),
    defaultValues: {
      phone: prefill_data?.mobile ?? '',
    },
  });

  useEffect(() => {
    publishEvent({
      category: mapPageToCategory(window.location.pathname),
      action: GaEvents.pageLand.login,
    });
    publishEventParameter(
      MoeEvents.pageLand.login,
      makeBkfEventParameterPayload(),
    );
  }, []);

  const [isMobileUpdated, setIsMobileUpdated] = useState(false);
  const onSubmit = async (data: LoginFormValues) => {
    onFormSubmit(data, isMobileUpdated, (message: string) =>
      form.setError('root', { message }),
    );
  };

  return (
    <div
      id="login-modal"
      className={`w-screen h-screen flex flex-col
        space-y-10 items-center bg-white p-6 sm:p-0
        text-typography-primary
        sm:w-[800px] sm:h-[610px] sm:rounded-[20px]
        sm:flex-row sm:space-y-0 sm:items-start overflow-hidden
      `}
    >
      <AuthCloseCmp close_modal={closeModal} />
      <AuthBanner
        auth_state="login"
        banner={loginModal.banner}
        mobileBanner={loginModal.mobileBanner}
      />
      <div
        className={`flex flex-col
                     w-full
                    sm:px-14 sm:py-16
                    items-start justify-start lg:h-full
                `}
      >
        <AuthTextDescCmp
          text={loginModal.title}
          desc={loginModal.description}
        />
        <LoginFormFields
          form={form}
          on_submit={onSubmit}
          set_is_mobile_updated={setIsMobileUpdated}
          prefill_data={prefill_data}
        />
      </div>
    </div>
  );
};

export default LoginModal;
